'use client'

import { cn } from '@heroui/react'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface TestimonialReview {
  id: string
  authorName: string
  authorPhotoUrl: string | null
  rating: number
  comment: string
  reviewCreatedAt: string
}

interface TestimonialsCarouselProps {
  reviews: TestimonialReview[]
  averageRating: number
  totalReviewCount: number
}

function StarRating({ rating }: { rating: number }) {
  // Ink, not sun: sun is 1.70:1 on a white/paper card, invisible as text
  // here (design-system brand-recolour handoff, 2026-09-08).
  return (
    <span className="text-xs text-[color:var(--cw-ink)]" aria-label={`${rating} out of 5 stars`}>
      {'★'.repeat(rating)}
      {'☆'.repeat(5 - rating)}
    </span>
  )
}

// Official Google "G" mark, reproduced from Google's published brand SVG so
// the badge shows the real logo in colour, not a solid brand-blue fill.
function GoogleG() {
  return (
    <svg viewBox="0 0 48 48" className="h-4 w-4 shrink-0" aria-hidden="true">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  )
}

function ReviewCard({ review }: { review: TestimonialReview }) {
  return (
    <div
      className={cn(
        'w-[280px] flex-shrink-0 snap-start',
        'rounded-2xl border border-border bg-surface p-4'
      )}
    >
      {/* Paper chip carrying the full-colour platform mark, not a solid
          brand-blue pill: matches the design-system reviews spec, and
          drops the "Google blue" the retired-colour sweep flags. */}
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-[color:var(--cw-paper)] py-0.5 pl-1.5 pr-2">
          <GoogleG />
          <span className="text-[11px] font-semibold uppercase tracking-[.03em] text-[color:var(--cw-ink)]">
            Google
          </span>
        </span>
        <StarRating rating={review.rating} />
      </div>
      <p className="mb-3 line-clamp-4 text-xs leading-relaxed text-foreground">
        &ldquo;{review.comment}&rdquo;
      </p>
      <div className="text-xs font-semibold text-foreground">{review.authorName}</div>
    </div>
  )
}

export function TestimonialsCarousel({ reviews, averageRating, totalReviewCount }: TestimonialsCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const pageCount = Math.ceil(reviews.length / 3)

  const scrollToIndex = useCallback((index: number) => {
    if (!scrollRef.current) return
    const cardWidth = 280 + 12 // card width + gap
    scrollRef.current.scrollTo({ left: index * cardWidth * 3, behavior: 'smooth' })
    setActiveIndex(index)
  }, [])

  // Auto-advance
  useEffect(() => {
    if (typeof window === 'undefined') return
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced || isPaused || pageCount <= 1) return

    const interval = setInterval(() => {
      setActiveIndex(prev => {
        const next = (prev + 1) % pageCount
        scrollToIndex(next)
        return next
      })
    }, 5000)
    return () => clearInterval(interval)
  }, [isPaused, pageCount, scrollToIndex])

  // Track scroll position for dots
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const cardWidth = 280 + 12
    const scrollPos = scrollRef.current.scrollLeft
    const page = Math.round(scrollPos / (cardWidth * 3))
    setActiveIndex(Math.min(page, pageCount - 1))
  }, [pageCount])

  if (reviews.length === 0) return null

  return (
    <section className="py-12" aria-label="Customer testimonials">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="mb-6 text-center">
          <h2 className="cw-display-2 text-foreground">
            What Our Clients Say
          </h2>
          <p className="mt-1 text-sm text-muted">
            {averageRating.toFixed(1)} average from {totalReviewCount} Google reviews
          </p>
        </div>

        <div
          ref={scrollRef}
          className="scrollbar-hide flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3"
          onScroll={handleScroll}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocus={() => setIsPaused(true)}
          onBlur={() => setIsPaused(false)}
          role="region"
          aria-live="polite"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') scrollToIndex(Math.min(activeIndex + 1, pageCount - 1))
            if (e.key === 'ArrowLeft') scrollToIndex(Math.max(activeIndex - 1, 0))
          }}
        >
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        {pageCount > 1 && (
          <div className="mt-3 flex justify-center gap-1.5">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                aria-label={`Go to reviews page ${i + 1}`}
                className={cn(
                  'h-1 rounded-full transition-all',
                  i === activeIndex ? 'w-5 bg-accent' : 'w-2 bg-overlay'
                )}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
